// Timing and hit detection utilities - Position-based system

export type HitQuality = 'perfect' | 'good' | 'ok' | 'miss';

export interface DifficultyConfig {
  name: string;
  scrollSpeed: number;    // pixels per second (only difficulty variable)
  perfectWindow: number;  // ms (converted to pixels at runtime)
  goodWindow: number;     // ms
  okWindow: number;       // ms
}

export const DIFFICULTIES: Record<string, DifficultyConfig> = {
  easy: {
    name: 'Easy',
    perfectWindow: 300,
    goodWindow: 500,
    okWindow: 700,
    scrollSpeed: 100,
  },
  medium: {
    name: 'Medium',
    perfectWindow: 200,
    goodWindow: 350,
    okWindow: 500,
    scrollSpeed: 150,
  },
  hard: {
    name: 'Hard',
    perfectWindow: 150,
    goodWindow: 250,
    okWindow: 400,
    scrollSpeed: 220,
  },
  expert: {
    name: 'Expert',
    perfectWindow: 100,
    goodWindow: 180,
    okWindow: 300,
    scrollSpeed: 300,
  },
};

// Fixed morse spacing in pixels (standard ratios, never changes)
// Morse code timing ratios:
// - Dot = 1 unit
// - Dash = 3 units
// - Gap within letter = 1 unit
// - Gap between letters = 3 units
// - Gap between words = 7 units
const MORSE_UNIT_PIXELS = 40;

export const MORSE_SPACING = {
  dotWidth: MORSE_UNIT_PIXELS,           // 1 unit
  dashWidth: MORSE_UNIT_PIXELS * 3,      // 3 units
  symbolGap: MORSE_UNIT_PIXELS,          // 1 unit
  letterGap: MORSE_UNIT_PIXELS * 3,      // 3 units
  wordGap: MORSE_UNIT_PIXELS * 7,        // 7 units
  noteHeight: 32,
} as const;

// Pixel-based hit zones (converted from timing windows)
export interface PixelHitZones {
  perfectZone: number;  // pixels from hitZoneX
  goodZone: number;
  okZone: number;
}

export function computePixelHitZones(difficulty: DifficultyConfig): PixelHitZones {
  const { scrollSpeed, perfectWindow, goodWindow, okWindow } = difficulty;
  return {
    perfectZone: (perfectWindow / 1000) * scrollSpeed,
    goodZone: (goodWindow / 1000) * scrollSpeed,
    okZone: (okWindow / 1000) * scrollSpeed,
  };
}

export function getHitQualityByDistance(pixelOffset: number, hitZones: PixelHitZones): HitQuality {
  const absOffset = Math.abs(pixelOffset);
  if (absOffset <= hitZones.perfectZone) return 'perfect';
  if (absOffset <= hitZones.goodZone) return 'good';
  if (absOffset <= hitZones.okZone) return 'ok';
  return 'miss';
}

/**
 * Get points for a hit quality
 */
export function getHitPoints(quality: HitQuality): number {
  switch (quality) {
    case 'perfect': return 100;
    case 'good': return 75;
    case 'ok': return 50;
    case 'miss': return 0;
  }
}

/**
 * Get multiplier based on current streak
 */
export function getStreakMultiplier(streak: number): number {
  if (streak >= 50) return 4;
  if (streak >= 25) return 3;
  if (streak >= 10) return 2;
  return 1;
}
